import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Monitor } from 'lucide-react';
import { consentModeOf, CONSENT_MODES, type ConsentMode, type Placement } from '@dmn/shared';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { ChannelBadge } from '@/components/StatusBadge';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { TablePagination } from '@/components/TablePagination';
import { usePagination } from '@/hooks/use-pagination';
import { TableSearch } from '@/components/TableSearch';
import { useTableSearch } from '@/hooks/use-table-search';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { normalizeText } from '@/lib/utils';

export const Route = createFileRoute('/placements/')({ component: PlacementsIndex });

/**
 * The placement registry is owned jointly with the host products — the id is
 * hard-coded in their source. Creation therefore happens during an integration,
 * not from this screen; what an operator needs here is visibility and a kill
 * switch.
 */
function PlacementsIndex() {
  const queryClient = useQueryClient();
  const placements = useQuery({ queryKey: queryKeys.placements, queryFn: api.placements.list });

  /**
   * A placement PUT replaces the whole document, so every field has to be sent
   * or an unrelated one is reset. One builder rather than a literal per toggle,
   * the same reasoning as `patchCampaign` on the campaign page.
   */
  const patch = (placement: Placement, changes: Partial<Placement>) => ({
    id: placement.id,
    name: placement.name,
    surface: placement.surface,
    audience: placement.audience,
    channel: placement.channel,
    owningProduct: placement.owningProduct,
    requiresPriorConsent: placement.requiresPriorConsent,
    // Defaulted in `PlacementInput`, so omitting it would silently reset the
    // surface's consent stance on an unrelated toggle.
    consentMode: consentModeOf(placement),
    format: placement.format,
    status: placement.status,
    ...changes,
  });

  const toggle = useMutation({
    mutationFn: (placement: Placement) =>
      api.placements.update(
        placement.id,
        patch(placement, { status: placement.status === 'active' ? 'inactive' : 'active' })
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.placements }),
  });

  /**
   * Whether this surface needs consent at all — policy, and an operator's call.
   *
   * Distinct from `requiresPriorConsent` in the next column, which is a property
   * of the channel rather than a choice: an email cannot render a consent card,
   * so it can only ever serve people who opted in elsewhere. That one stays
   * read-only and code-owned. `provision` preserves this field, for the same
   * reason it preserves `status` — otherwise the next catalogue install silently
   * reverts it.
   */
  const setConsentMode = useMutation({
    mutationFn: ({ placement, consentMode }: { placement: Placement; consentMode: ConsentMode }) =>
      api.placements.update(placement.id, patch(placement, { consentMode })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.placements }),
  });

  const search = useTableSearch(placements.data, (p) => [
    p.id,
    p.name,
    p.surface,
    p.owningProduct,
    p.audience,
    p.channel,
    p.status,
  ]);
  const pagination = usePagination(search.filtered);
  return (
    <>
      <PageHeader title="Placements" description="Registered slots across DearDoc products" />
      <PageBody>
        <TableSearch
          search={search}
          placeholder="Search id, name, surface, product or channel"
          label="Search placements"
          noun="placement"
        />
        <Card className="shadow-none">
          <CardContent className="p-0">
            {placements.isError ? (
              <ErrorState error={placements.error} onRetry={() => void placements.refetch()} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Placement</TableHead>
                    <TableHead>Owning product</TableHead>
                    <TableHead>Audience</TableHead>
                    <TableHead>Consent mode</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Can ask here</TableHead>
                    <TableHead>Format limits</TableHead>
                    <TableHead className="text-right">Active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {placements.isPending && <LoadingRows columns={7} />}
                  {pagination.pageRows.map((placement) => (
                    <TableRow key={placement.id}>
                      <TableCell>
                        <p className="font-medium">{placement.name}</p>
                        <p className="font-mono text-xs text-muted-foreground">{placement.id}</p>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {placement.owningProduct}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{placement.audience.toUpperCase()}</Badge>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={consentModeOf(placement)}
                          disabled={setConsentMode.isPending}
                          onValueChange={(next) =>
                            setConsentMode.mutate({ placement, consentMode: next as ConsentMode })
                          }
                        >
                          <SelectTrigger className="h-8 w-[124px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CONSENT_MODES.map((mode) => (
                              <SelectItem key={mode} value={mode}>
                                {mode}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <ChannelBadge channel={placement.channel} />
                      </TableCell>
                      <TableCell>
                        {/*
                          Read-only, and not the same question as the column
                          before it. Consent mode is whether an answer is needed;
                          this is whether the surface can obtain one. A messaged
                          channel cannot render an interactive card, so it can
                          only serve people who opted in somewhere else — that is
                          a fact about the channel, not a lever.
                        */}
                        {placement.requiresPriorConsent ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="font-normal">
                                Prior opt-in
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              This surface cannot render a consent card, so the person must have
                              opted in beforehand. Unknown consent means no unit.
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="font-normal">
                                Can ask
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              This surface can render the consent card in line, so somebody who has
                              not answered is asked rather than skipped.
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {placement.format.maxHeadlineChars}/{placement.format.maxBodyChars} chars
                        {placement.format.supportsImage ? ' · image' : ' · text only'}
                        {placement.format.imageAspect
                          ? ` ${placement.format.imageAspect.width}×${placement.format.imageAspect.height}`
                          : ''}
                        {placement.format.layout ? ` · ${placement.format.layout}` : ''}
                      </TableCell>
                      <TableCell className="text-right">
                        <Switch
                          checked={placement.status === 'active'}
                          disabled={toggle.isPending}
                          onCheckedChange={() => toggle.mutate(placement)}
                          aria-label={`Toggle ${placement.name}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <TablePagination pagination={pagination} label="placements" />
            {placements.data?.length === 0 && (
              <EmptyState
                title="No placements registered"
                description="Placement ids are chosen by the integrating product and registered via the API during integration."
                icon={Monitor}
              />
            )}
          </CardContent>
        </Card>
        <p className="text-xs text-muted-foreground">
          Surfaces:{' '}
          {[...new Set((placements.data ?? []).map((p) => p.surface))]
            .map((surface) => normalizeText(surface))
            .join(' · ') || '—'}
        </p>
      </PageBody>
    </>
  );
}
